import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Bitcoin, Gem, Hexagon, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ASSETS = {
  bitcoin: { label: 'Bitcoin', pair: 'BTC / USD', color: '#f59e0b', glow: 'rgba(245,158,11,0.5)', icon: Bitcoin },
  gold: { label: 'Gold', pair: 'XAU (PAXG) / USD', color: '#eab308', glow: 'rgba(234,179,8,0.5)', icon: Gem },
  ethereum: { label: 'Ethereum', pair: 'ETH / USD', color: '#8b9cf7', glow: 'rgba(139,156,247,0.5)', icon: Hexagon },
};

const LiveMarketChart = () => {
  const [asset, setAsset] = useState('bitcoin');
  const [chartData, setChartData] = useState([]);
  const [price, setPrice] = useState(null);
  const [change, setChange] = useState(null);
  const [high24h, setHigh24h] = useState(null);
  const [low24h, setLow24h] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState('connecting'); // connecting | live | delayed
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const priceRef = useRef({});
  const theme = ASSETS[asset];
  const assetRef = useRef(asset);
  assetRef.current = asset;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const fetchPrice = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/market/price?asset=${asset}`);
      if (!res.ok) throw new Error('price failed');
      const d = await res.json();
      if (assetRef.current !== asset) return;
      const prev = priceRef.current[asset];
      setPrice(d.usd);
      setChange((d.usd_24h_change ?? 0).toFixed(2));
      setHigh24h(d.usd_24h_high);
      setLow24h(d.usd_24h_low);
      setLastUpdated(new Date());
      setStatus(d.live ? 'live' : 'delayed');
      priceRef.current[asset] = d.usd;

      if (prev && prev !== d.usd) {
        const el = document.getElementById('market-price');
        if (el) {
          el.classList.add(d.usd > prev ? 'flash-up' : 'flash-down');
          setTimeout(() => el.classList.remove('flash-up', 'flash-down'), 600);
        }
      }
    } catch (err) {
      setStatus((s) => (s === 'connecting' ? 'connecting' : 'delayed'));
    }
  }, [asset]);

  const fetchChart = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/market/chart?asset=${asset}&days=7`);
      if (!res.ok) throw new Error('chart failed');
      const d = await res.json();
      if (assetRef.current !== asset) return;
      if (d.prices && d.prices.length) {
        setChartData(
          d.prices.map(([timestamp, p]) => ({
            time: new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            price: p,
            fullDate: new Date(timestamp).toLocaleDateString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric',
            }),
          }))
        );
      }
    } catch (err) {
      // keep previous chart
    } finally {
      if (assetRef.current === asset) setIsLoading(false);
    }
  }, [asset]);

  // Refetch everything when asset changes
  useEffect(() => {
    fetchPrice();
    fetchChart();
  }, [fetchPrice, fetchChart]);

  // Live price polling every 15s
  useEffect(() => {
    const id = setInterval(fetchPrice, 15000);
    return () => clearInterval(id);
  }, [fetchPrice]);

  // Chart refresh every 2 minutes
  useEffect(() => {
    const id = setInterval(fetchChart, 120000);
    return () => clearInterval(id);
  }, [fetchChart]);

  const formatPrice = (value) => {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: value < 10 ? 2 : 0,
      maximumFractionDigits: value < 10 ? 4 : 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/95 border border-white/15 backdrop-blur-sm px-4 py-3 rounded-lg shadow-xl">
          <p className="text-white/60 text-[10px] md:text-xs mb-1">{payload[0].payload.fullDate}</p>
          <p className="text-base md:text-lg font-medium" style={{ color: theme.color }}>
            {formatPrice(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const StatusBadge = () => {
    if (status === 'live') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/10" data-testid="market-status-live">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          <span className="text-green-400 text-[9px] md:text-[10px] font-medium tracking-wide">LIVE</span>
        </div>
      );
    }
    if (status === 'delayed') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10" data-testid="market-status-delayed">
          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
          <span className="text-amber-400 text-[9px] md:text-[10px] font-medium tracking-wide">LIVE · CACHED</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10" data-testid="market-status-connecting">
        <RefreshCw className="w-2.5 h-2.5 text-white/60 animate-spin" />
        <span className="text-white/60 text-[9px] md:text-[10px] font-medium tracking-wide">CONNECTING</span>
      </div>
    );
  };

  return (
    <section ref={sectionRef} className="relative bg-[#050505] py-14 md:py-20 lg:py-28 overflow-hidden" data-testid="live-market-chart">
      {/* Background glow following active asset */}
      <div
        className="absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] blur-3xl transition-colors duration-700"
        style={{ background: `radial-gradient(circle, ${theme.glow} 0%, transparent 70%)`, opacity: 0.12 }}
      />
      <div className="absolute bottom-0 left-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-radial from-orange-900/5 via-transparent to-transparent blur-3xl" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16">
        {/* Section intro */}
        <div
          className="mb-6 md:mb-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-2">
            [ LIVE MARKETS ]
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-tight">
            Real-Time Prices Across <span className="text-amber-400">Gold &amp; Crypto</span>
          </h2>
        </div>

        {/* Asset Tabs */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 100ms',
          }}
        >
          {Object.entries(ASSETS).map(([key, a]) => {
            const Icon = a.icon;
            const active = asset === key;
            return (
              <button
                key={key}
                onClick={() => setAsset(key)}
                data-testid={`asset-tab-${key}`}
                className={`group flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full border text-[11px] md:text-sm transition-all duration-500 ${
                  active
                    ? 'asset-tab-active bg-white/[0.06] border-white/20 text-white'
                    : 'bg-white/[0.02] border-white/[0.08] text-white/50 hover:text-white/80 hover:border-white/15'
                }`}
                style={active ? { boxShadow: `0 8px 30px ${a.glow}` } : undefined}
              >
                <Icon
                  className="w-3.5 h-3.5 md:w-4 md:h-4 transition-colors duration-500"
                  style={{ color: active ? a.color : undefined }}
                />
                <span className="font-medium tracking-wide">{a.label}</span>
              </button>
            );
          })}
        </div>

        {/* Price Header */}
        <div
          className="flex items-start justify-between flex-wrap gap-4 mb-5 md:mb-7"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 200ms',
          }}
        >
          <div className="flex items-center gap-3 md:gap-4">
            <div
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition-all duration-500"
              style={{ borderColor: `${theme.color}55`, background: `${theme.color}1a` }}
            >
              <theme.icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: theme.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg md:text-2xl lg:text-3xl font-light text-white">{theme.label}</h3>
                <StatusBadge />
              </div>
              <p className="text-white/40 text-[10px] md:text-sm">{theme.pair} • 7-Day Chart</p>
            </div>
          </div>

          <div className="text-right">
            <p
              id="market-price"
              data-testid="market-price"
              className="text-2xl md:text-3xl lg:text-4xl font-light text-white transition-colors duration-300 tabular-nums"
            >
              {formatPrice(price)}
            </p>
            {change != null && (
              <div className={`flex items-center justify-end gap-1 mt-0.5 ${parseFloat(change) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {parseFloat(change) >= 0 ? <TrendingUp className="w-3 h-3 md:w-4 md:h-4" /> : <TrendingDown className="w-3 h-3 md:w-4 md:h-4" />}
                <span className="text-[10px] md:text-sm font-medium">{change}%</span>
                <span className="text-white/40 text-[9px] md:text-xs">24h</span>
              </div>
            )}
          </div>
        </div>

        {/* Chart */}
        <div
          className="bg-black/40 border border-white/[0.06] rounded-xl p-4 md:p-6 lg:p-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 300ms',
          }}
        >
          <div className="h-[200px] md:h-[300px] lg:h-[360px]">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="marketGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.color} stopOpacity={0.45} />
                      <stop offset="55%" stopColor={theme.color} stopOpacity={0.12} />
                      <stop offset="100%" stopColor={theme.color} stopOpacity={0} />
                    </linearGradient>
                    <filter id="marketGlow">
                      <feGaussianBlur stdDeviation="2.5" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis
                    domain={['auto', 'auto']}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                    tickFormatter={(v) => (v >= 10000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v).toLocaleString('en-US')}`)}
                    width={52}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke={theme.color}
                    strokeWidth={2}
                    fill="url(#marketGradient)"
                    filter="url(#marketGlow)"
                    animationDuration={1400}
                    animationEasing="ease-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center gap-2 text-white/40">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading chart…</span>
              </div>
            )}
          </div>

          {/* Stat row */}
          <div className="mt-4 md:mt-6 grid grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 md:p-4">
              <p className="text-white/40 text-[9px] md:text-xs mb-1">24h High</p>
              <p className="text-green-400/90 text-sm md:text-lg font-light tabular-nums">{formatPrice(high24h)}</p>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 md:p-4">
              <p className="text-white/40 text-[9px] md:text-xs mb-1">24h Low</p>
              <p className="text-red-400/90 text-sm md:text-lg font-light tabular-nums">{formatPrice(low24h)}</p>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 md:p-4">
              <p className="text-white/40 text-[9px] md:text-xs mb-1">24h Change</p>
              <p className={`text-sm md:text-lg font-light tabular-nums ${change != null && parseFloat(change) >= 0 ? 'text-green-400/90' : 'text-red-400/90'}`}>
                {change != null ? `${change}%` : '—'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 md:gap-4">
              <p className="text-white/30 text-[9px] md:text-xs">Data: Kraken · CoinGecko</p>
              {lastUpdated && (
                <p className="text-white/20 text-[9px] md:text-xs">Updated: {lastUpdated.toLocaleTimeString()}</p>
              )}
            </div>
            <button
              onClick={() => { fetchPrice(); fetchChart(); }}
              disabled={isLoading}
              data-testid="market-refresh-btn"
              className="flex items-center gap-1.5 md:gap-2 text-white/40 hover:text-amber-400 text-[10px] md:text-xs transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-2.5 h-2.5 md:w-3 md:h-3 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveMarketChart;
